import { CartUpdateEvent } from '@theme/events';
import { buildDeliveryDayArray } from '@theme/date-time-utils';

// Constants
const CART_ATTRIBUTES = {
  DELIVERY_POSTCODE: 'delivery_postcode',
  SELECTED_DELIVERY_TIME: 'selected_delivery_time',
  SELECTED_TIMESLOT_HANDLE: 'selected_timeslot_handle',
  VERZENDMETHODE_TITEL: 'verzendmethode_titel',
  DELIVERY_DAY: 'delivery_day'
};

const API_ENDPOINTS = {
  CART_DATA: '/cart.js',
  CART_UPDATE: '/cart/update.js'
};

const SELECTORS = {
  CART_DRAWER: 'cart-drawer-component',
  CART_ITEMS: 'cart-items-component',
  MAIN_CONTENT: '#MainContent',
  SHOPIFY_SECTIONS: '[id^="shopify-section-"]',
  COLLECTION_FOOTER: 'custom-collection-footer-component'
};

const TIME_PATTERNS = {
  ISO_FORMAT: /T.*[Z+]/,
  SIMPLE_TIME: /^\d{1,2}:\d{2}\s*(am|pm)$/i
};

const CONSOLE_PREFIX = '[DELIVERY-CART-ATTRIBUTES]';

/** Coalesces concurrent GET /cart.js reads (multiple features on collection load). */
let getCartDataInflight = /** @type {Promise<Record<string, unknown> | null> | null} */ (null);

// Helper Functions
/**
 * Log message with consistent prefix
 * @param {any} level - Log level (log, warn, error)
 * @param {string} message - Message to log
 * @param {...any} args - Additional arguments
 */
function logMessage(level, message, ...args) {
  // /** @type {any} */ (console)[level](`🔍 ${CONSOLE_PREFIX} ${message}`, ...args);
}

/**
 * Get element by selector with type safety
 * @param {string} selector - CSS selector
 * @returns {HTMLElement|null} - The element or null
 */
function getElement(selector) {
  const element = document.querySelector(selector);
  return element instanceof HTMLElement ? element : null;
}

/**
 * Get all elements by selector with type safety
 * @param {string} selector - CSS selector
 * @returns {HTMLElement[]} - Array of elements
 */
function getElements(selector) {
  return Array.from(document.querySelectorAll(selector)).filter(el => el instanceof HTMLElement);
}

/**
 * Get dataset value safely
 * @param {HTMLElement} element - The element
 * @param {string} key - Dataset key
 * @returns {string|undefined} - Dataset value or undefined
 */
function getDatasetValue(element, key) {
  return element.dataset[key];
}

/**
 * Extract section ID from element
 * @param {HTMLElement} element - Element with section ID
 * @returns {string|null} - Section ID or null
 */
function extractSectionId(element) {
  const sectionId = getDatasetValue(element, 'sectionId');
  return sectionId || null;
}

/**
 * Check if value is meaningful (not empty)
 * @param {any} value - Value to check
 * @returns {boolean} - True if value is meaningful
 */
function isMeaningfulValue(value) {
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  return Boolean(value && typeof value === 'string' && value.trim() !== '');
}

/**
 * Filter null values from attributes object
 * @param {Record<string, any>} attributes - Attributes object
 * @returns {Record<string, any>} - Filtered attributes object
 */
function filterNullValues(attributes) {
  const filteredAttributes = /** @type {Record<string, any>} */ ({});
  for (const [key, value] of Object.entries(attributes)) {
    filteredAttributes[key] = value === null ? '' : value;
  }
  return filteredAttributes;
}

/**
 * Prepare request body for cart update
 * @param {Record<string, any>} attributes - Attributes to update
 * @param {Array<string>} sections - Sections to include
 * @returns {Record<string, any>} - Request body
 */
function prepareRequestBody(attributes, sections) {
  const requestBody = /** @type {Record<string, any>} */ ({ 
    attributes: filterNullValues(attributes) 
  });
  
  if (sections.length > 0) {
    requestBody.sections = sections.join(',');
    requestBody.sections_url = window.location.pathname;
  }
  
  return requestBody;
}

/**
 * Trigger cart update event
 * @param {any} responseData - Response data from cart update
 * @param {Array<string>} sections - Sections that were updated
 */
function triggerCartUpdateEvent(responseData, sections) {
  if (sections.length > 0 && responseData.sections) {
    const cartUpdateEvent = new CartUpdateEvent({}, 'cart-attributes-update', {
      source: 'cart-attributes-update',
      sections: responseData.sections
    });
    document.dispatchEvent(cartUpdateEvent);
  }
}

/**
 * Get cart drawer section ID
 * @returns {string|null} - Section ID or null
 */
function getCartDrawerSectionId() {
  const cartDrawer = getElement(SELECTORS.CART_DRAWER);
  return cartDrawer ? extractSectionId(cartDrawer) : null;
}

/**
 * Get cart items section IDs
 * @returns {Array<string>} - Array of section IDs
 */
function getCartItemsSectionIds() {
  const cartItemsComponents = getElements(SELECTORS.CART_ITEMS);
  return cartItemsComponents
    .map(extractSectionId)
    .filter((/** @type {string|null} */ id) => id !== null);
}

/**
 * Get collection footer section IDs
 * @returns {Array<string>} - Array of section IDs
 */
function getCollectionFooterSectionIds() {
  const mainContent = getElement(SELECTORS.MAIN_CONTENT);
  if (!mainContent) return [];

  const sectionsWithCollectionFooter = mainContent.querySelectorAll(
    `${SELECTORS.SHOPIFY_SECTIONS}:has(${SELECTORS.COLLECTION_FOOTER})`
  );
  
  return Array.from(sectionsWithCollectionFooter)
    .map(section => section.id.replace('shopify-section-', ''))
    .filter(id => id);
}


export class DeliveryCartAttributes {
  /**
   * Get current cart data from Shopify API
   * @returns {Promise<Object|null>} Cart data or null if failed
   */
  static async getCartData() {
    logMessage('log', 'Getting cart data');
    if (!getCartDataInflight) {
      getCartDataInflight = fetch(API_ENDPOINTS.CART_DATA)
        .then(async (response) => {
          if (response.ok) return /** @type {Record<string, unknown>} */ (await response.json());
          return null;
        })
        .catch((error) => {
          logMessage('error', 'Error fetching cart data:', error);
          return null;
        })
        .finally(() => {
          getCartDataInflight = null;
        });
    }
    return getCartDataInflight;
  }

  /**
   * Get cart attributes safely with type checking
   * @param {Object} cart - Cart data object
   * @returns {Object} Cart attributes object
   */
  static #getCartAttributes(cart) {
    const cartData = /** @type {any} */ (cart);
    return /** @type {any} */ (cartData?.attributes || {});
  }

  /**
   * Check if a cart attribute exists and has a meaningful value
   * @param {string} attributeName - The name of the attribute to check
   * @returns {Promise<boolean>} True if attribute exists and has value
   */
  static async hasCartAttribute(attributeName) {
    const cart = await this.getCartData();
    if (!cart) return false;

    const attributes = this.#getCartAttributes(cart);
    const value = /** @type {any} */ (attributes)[attributeName];
    return isMeaningfulValue(value);
  }

  /**
   * Get specific cart attribute value
   * @param {string} attributeName - The name of the attribute to get
   * @param {any} [cart] - Optional cart data to avoid redundant API calls
   * @returns {Promise<string|Array<string>>} Attribute value or empty string/array
   */
  static async getCartAttribute(attributeName, cart = undefined) {
    if (!cart) {
      cart = await this.getCartData();
    }
    if (!cart) return '';

    const attributes = this.#getCartAttributes(cart);
    return /** @type {any} */ (attributes)[attributeName] || '';
  }

  /**
   * Check if postal code exists in cart attributes
   * @param {any} [cart] - Optional cart data to avoid redundant API calls
   * @returns {Promise<boolean>} True if postal code is set
   */
  static async hasPostalCode(cart = undefined) {
    if (cart) {
      const attributes = this.#getCartAttributes(cart);
      return isMeaningfulValue(/** @type {any} */ (attributes)[CART_ATTRIBUTES.DELIVERY_POSTCODE]);
    }
    return await this.hasCartAttribute(CART_ATTRIBUTES.DELIVERY_POSTCODE);
  }

  /**
   * Check if delivery time exists in cart attributes
   * @param {any} [cart] - Optional cart data to avoid redundant API calls
   * @returns {Promise<boolean>} True if delivery time is set
   */
  static async hasDeliveryTime(cart = undefined) {
    if (cart) {
      const attributes = this.#getCartAttributes(cart);
      return isMeaningfulValue(/** @type {any} */ (attributes)[CART_ATTRIBUTES.SELECTED_DELIVERY_TIME]);
    }
    return await this.hasCartAttribute(CART_ATTRIBUTES.SELECTED_DELIVERY_TIME);
  }

  /**
   * Check if postal code and delivery time exist in cart attributes
   * @returns {Promise<{hasData: boolean, cart: any}>} Object with result and cart data
   */
  static async hasPostalCodeAndDeliveryTime() {
    const cart = await this.getCartData();
    if (!cart) return { hasData: false, cart: null };

    // Use the cart data we already fetched and pass it to the helper methods
    const hasData = await this.hasPostalCode(cart) && await this.hasDeliveryTime(cart);
    return { hasData, cart };
  }

  /**
   * Get current delivery day from cart attributes
   * @param {any} [cart] - Optional cart data to avoid redundant API calls
   * @returns {Promise<string|null>} Current delivery day or null if not found
   */
  static async getCurrentDeliveryDay(cart = undefined) {
    // Get the selected timeslot handle which contains the day name
    const selectedTimeslotHandle = await this.getCartAttribute(CART_ATTRIBUTES.SELECTED_TIMESLOT_HANDLE, cart);
    
    if (selectedTimeslotHandle && typeof selectedTimeslotHandle === 'string' && selectedTimeslotHandle.trim() !== '') {
      // Extract day name from timeslot handle (e.g., "woensdag-17-9-15-h-22-h-externe-bezorging" -> "woensdag")
      const dayMatch = selectedTimeslotHandle.match(/^(maandag|woensdag)/i);
      if (dayMatch && dayMatch[1]) {
        const dutchDay = dayMatch[1].toLowerCase();
        // Convert Dutch day names to English for consistency
        const dayMapping = {
          'maandag': 'Monday',
          'woensdag': 'Wednesday'
        };
        const deliveryDay = /** @type {any} */ (dayMapping)[dutchDay] || dutchDay;
        
        logMessage('log', `Found delivery day from timeslot handle: ${deliveryDay} (from ${dutchDay})`);
        return deliveryDay;
      }
    }
    
    return null;
  }

  /**
   * Update cart attributes with sections support
   * @param {Object} attributes - Attributes to update
   * @param {Array<string>} [sections] - Optional sections to include in the response
   * @returns {Promise<boolean>} - Success status
   */
  static async updateCartAttributes(attributes, sections = []) {
    try {
      const requestBody = prepareRequestBody(attributes, sections);

      const response = await fetch(API_ENDPOINTS.CART_UPDATE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });
      
      if (response.ok) {
        const responseData = await response.json();
        triggerCartUpdateEvent(responseData, sections);
      }
      
      return response.ok;
    } catch (error) {
      logMessage('error', 'Error updating cart attributes:', error);
      return false;
    }
  }

  /**
   * Get cart sections that need to be updated
   * @returns {Array<string>} Array of section IDs
   */
  static getCartSectionsToUpdate() {
    const sections = [];
    
    // Add cart drawer section
    const cartDrawerSectionId = getCartDrawerSectionId();
    if (cartDrawerSectionId) {
      sections.push(cartDrawerSectionId);
    }
    
    // Add cart items sections
    sections.push(...getCartItemsSectionIds());

    // Add collection footer sections
    sections.push(...getCollectionFooterSectionIds());

    return sections;
  }

  /**
   * Clear delivery-related cart attributes
   * @returns {Promise<boolean>} Success status
   */
  static async clearDeliveryAttributes() {
    return await this.updateCartAttributes({
      [CART_ATTRIBUTES.SELECTED_DELIVERY_TIME]: '',
      [CART_ATTRIBUTES.SELECTED_TIMESLOT_HANDLE]: '',
      [CART_ATTRIBUTES.VERZENDMETHODE_TITEL]: '',
      [CART_ATTRIBUTES.DELIVERY_DAY]: ''
    });
  }

  /**
   * Set postal code and clear delivery attributes
   * @param {string} postcode - The postal code to set
   * @param {Array<string>} [sections] - Sections to update
   * @returns {Promise<boolean>} Success status
   */
  static async setPostalCodeAndClearDelivery(postcode, sections = []) {
    return await this.updateCartAttributes({
      [CART_ATTRIBUTES.DELIVERY_POSTCODE]: postcode,
      [CART_ATTRIBUTES.SELECTED_DELIVERY_TIME]: '',
      [CART_ATTRIBUTES.SELECTED_TIMESLOT_HANDLE]: '',
      [CART_ATTRIBUTES.VERZENDMETHODE_TITEL]: ''
    }, sections);
  }

  /**
   * Set delivery time selection
   * @param {any} timeData - Delivery time data
   * @param {Array<string>} [sections] - Sections to update
   * @returns {Promise<boolean>} Success status
   */
  static async setDeliveryTime(timeData, sections = []) {
    logMessage('log', 'Setting delivery time:', timeData);
    
    const deliveryDayArray = buildDeliveryDayArray(timeData);
    
    const attributes = {
      [CART_ATTRIBUTES.SELECTED_DELIVERY_TIME]: timeData.metaobjectId,
      [CART_ATTRIBUTES.SELECTED_TIMESLOT_HANDLE]: timeData.metaobjectHandle,
      [CART_ATTRIBUTES.VERZENDMETHODE_TITEL]: timeData.verzendmethodeHandle,
      [CART_ATTRIBUTES.DELIVERY_DAY]: deliveryDayArray
    };
    return await this.updateCartAttributes(attributes, sections);
  }

  /**
   * Update only the delivery day (keep other attributes)
   * @param {Array<string>|string} deliveryDay - The delivery day to set (array of ISO times or string)
   * @returns {Promise<boolean>} Success status
   */
  static async updateDeliveryDay(deliveryDay) {
    return await this.updateCartAttributes({
      [CART_ATTRIBUTES.DELIVERY_DAY]: deliveryDay
    });
  }
}

// Export for global access (maintaining backward compatibility)
if (typeof window !== "undefined") {
  /** @type {any} */ (window).DeliveryCartAttributes = DeliveryCartAttributes;
  
  // Backward compatibility aliases
  /** @type {any} */ (window).SimpleCartAttributes = {
    updateCartAttributes: DeliveryCartAttributes.updateCartAttributes
  };
  
  /** @type {any} */ (window).CartAttributesUtils = {
    getCartData: DeliveryCartAttributes.getCartData,
    hasPostalCodeAndDeliveryTime: DeliveryCartAttributes.hasPostalCodeAndDeliveryTime,
    hasPostalCode: DeliveryCartAttributes.hasPostalCode,
    getCartAttribute: DeliveryCartAttributes.getCartAttribute,
    hasCartAttribute: DeliveryCartAttributes.hasCartAttribute
  };

  // Also maintain DeliveryCart for compatibility
  /** @type {any} */ (window).DeliveryCart = DeliveryCartAttributes;
}
